/* eslint-disable no-unused-vars */
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import style from './avatarUpload.module.scss';
import cx from 'classnames';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import FilePondPluginImageEdit from 'filepond-plugin-image-edit';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { BASE_URL, currentAuthHeader } from 'api/Api';
import { FilePondFile } from 'filepond';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { freezeResult, selectUser, updateAvatar } from 'store/auth/authSlice';
import { User } from 'store/types';
import CropperConnector from 'components/modal/croppModal/cropperConnector';
import CropperComponent, {
  CropperRef,
} from 'components/modal/croppModal/croppModal';
import UploadModal from 'components/modal/uploadModal';
import { setAvatarFile } from 'store/transitionSlice';

const deleteHTML =
  '<style>#icon_delete:hover{box-shadow: 0 0 0 0.125em hsl(0deg 0% 100% / 90%) !important;}</style><div style="box-shadow: 0 0 0 0 hsl(0deg 0% 100% / 90%)" id="icon_delete"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="var(--error)"><path d="M5.00293 20C5.00293 21.103 5.89993 22 7.00293 22H17.0029C18.1059 22 19.0029 21.103 19.0029 20V8H21.0029V6H18.0029H17.0029V4C17.0029 2.897 16.1059 2 15.0029 2H9.00293C7.89993 2 7.00293 2.897 7.00293 4V6H6.00293H3.00293V8H5.00293V20ZM9.00293 4H15.0029V6H9.00293V4ZM8.00293 8H16.0029H17.0029L17.0039 20H7.00293V8H8.00293Z"></path><path d="M9.00293 10H11.0029V18H9.00293V10ZM13.0029 10H15.0029V18H13.0029V10Z"></path></svg></div>';

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginImageTransform,
  FilePondPluginImageCrop,
  FilePondPluginImageEdit
);

export type SendRef = {
  send: () => void;
  click: () => void;
  setFile: (newF: File) => void;
};

type AvatarUploadProps = {
  onUpdatefiles: (files: FilePondFile[]) => void;
  onProcessStart?: () => void;
  onProcessEnd?: () => void;
  className?: string;
  onDelete: () => void;
  isDeleted: boolean;
};

const AvatarUpload = React.forwardRef<SendRef, AvatarUploadProps>(
  (
    {
      onUpdatefiles,
      onProcessStart,
      onProcessEnd,
      className,
      onDelete,
      isDeleted,
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const inputRef = useRef<FilePond>(null);
    const cropRef = useRef<CropperRef>(null);
    const [cropperConnector] = useState<CropperConnector>(
      new CropperConnector(cropRef)
    );
    const [currentModal, setCurrentModal] = useState<null | React.ReactElement>(
      null
    );

    useImperativeHandle(ref, () => ({
      send: async () => {
        if (inputRef.current && user) {
          dispatch(freezeResult());
          inputRef.current.processFiles();
        }
      },
      click: () => {
        if (inputRef.current) {
          inputRef.current.browse();
        }
      },
      setFile: (newF: File) => {
        if (inputRef.current) {
          inputRef.current.removeFiles();
          inputRef.current.addFile(newF);
        }
      },
    }));

    useEffect(() => {
      const label = document.querySelector<HTMLDivElement>(
        '.filepond--drop-label'
      );

      const listener = (e: MouseEvent) => {
        const target: any = e.target;

        if (target.id === 'icon_delete' || target.closest('#icon_delete')) {
          e.preventDefault();
          e.stopPropagation();

          onDelete();
        } else if (!inputRef?.current?.getFiles?.length) {
          e.preventDefault();
          e.stopPropagation();

          setCurrentModal(
            <UploadModal
              onClose={() => {
                setCurrentModal(null);
              }}
              setter={setAvatarFile}
              isMultiple={false}
            ></UploadModal>
          );
        }
      };

      label?.addEventListener('click', listener, true);

      return () => {
        label?.removeEventListener('click', listener, true);
      };
    }, []);

    return (
      <div className={cx(style.container, className)}>
        {currentModal !== null && currentModal}
        <CropperComponent
          ref={cropRef}
          cropperConnector={cropperConnector}
          replaceFile={(oldF, newF) => {
            if (inputRef.current && newF && oldF) {
              inputRef.current.removeFile(oldF.name);
              inputRef.current.addFile(newF);
            }
          }}
          initialAspectRatio={1}
        ></CropperComponent>
        <FilePond
          imageEditEditor={cropperConnector}
          onupdatefiles={(files) => {
            onUpdatefiles(files);
          }}
          allowMultiple={false}
          instantUpload={false}
          name="avatar"
          ref={inputRef}
          labelFileProcessing="Идет загрузка"
          labelFileProcessingComplete="Успешно загружено"
          labelFileProcessingError="Произошла ошибка"
          labelIdle={
            user?.avatar && !isDeleted
              ? `<image src=${user.avatar}>${deleteHTML}`
              : `<div style="font-size: 80px;">${user?.name
                  .slice(0, 1)[0]
                  .toUpperCase()}${user?.surname
                  .slice(0, 1)[0]
                  .toUpperCase()}</div>`
          }
          imageCropAspectRatio="1:1"
          stylePanelLayout="compact circle"
          styleLoadIndicatorPosition="center bottom"
          styleProgressIndicatorPosition="center bottom"
          styleButtonRemoveItemPosition="center bottom"
          styleImageEditButtonEditItemPosition="center center"
          allowProcess={false}
          allowRevert={false}
          onprocessfile={() => {
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.removeFiles();
              }
            }, 1000);
          }}
          server={{
            url: BASE_URL,
            process: async (
              fieldName,
              file,
              metadata,
              load,
              error,
              progress,
              abort
            ) => {
              if (user) {
                onProcessStart && onProcessStart();

                const xhr = new XMLHttpRequest();
                xhr.open('POST', `${BASE_URL}/user/avatar/${user._id}`);
                xhr.setRequestHeader('Authorization', currentAuthHeader);
                xhr.responseType = 'json';

                const formData = new FormData();
                formData.append(fieldName, file, file.name);

                xhr.upload.onprogress = (e) => {
                  progress(e.lengthComputable, e.loaded, e.total);
                };

                const promise = new Promise<User>((resolve, reject) => {
                  xhr.onload = function () {
                    if (xhr.status >= 200 && xhr.status < 300) {
                      load('Успешно загружено');
                      resolve(xhr.response);
                    } else {
                      error('Ошибка');
                      resolve(xhr.response);
                    }

                    onProcessEnd && onProcessEnd();
                  };

                  xhr.onerror = function () {
                    error('Ошибка');
                    onProcessEnd && onProcessEnd();
                    reject(xhr.response);
                  };

                  xhr.send(formData);
                });

                dispatch(updateAvatar(promise));

                return {
                  abort: () => {
                    xhr.abort();
                    abort();
                  },
                };
              }
            },
          }}
        />
      </div>
    );
  }
);
AvatarUpload.displayName = 'AvatarUpload';

export default AvatarUpload;
